import { FC } from "react";
import Link from "next/link";

import { UserConnectChargeEnable, UserLoginStatus } from "@/apis/clients";
import { PaypalAccount } from "@/apis/clients";
import { Button } from "@/components/atoms/buttons/Button";
import { ButtonText } from "@/components/atoms/buttons/ButtonText";
import { AccountsListCard } from "@/components/organisms/accounts/AccountsListCard";
import { CreditStatusLabel } from "@/components/organisms/accounts/index/CreditStatusLabel";

interface Props {
  handleCreatePaypalAccount: () => void | Promise<void>;
  handleDeletePaypalAccount: () => void | Promise<void>;
  paypalAccount: PaypalAccount;
}

export const PaypalConnectInfo: FC<Props> = ({ paypalAccount, handleCreatePaypalAccount, handleDeletePaypalAccount }) => {
  if (paypalAccount.user_login_status !== UserLoginStatus.MASTER) {
    return <></>;
  }

  const isConnectApplying = () => {
    return paypalAccount.user_payment_paypal;
  };

  const isChargeEnabled = () => {
    return paypalAccount.user_payment_paypal && paypalAccount.user_payment_paypal?.charges_enabled === UserConnectChargeEnable.ENABLED;
  };

  const connectTitle = (text: string) => {
    return (
      <>
        {text}
        {isChargeEnabled() && (
          <ButtonText
            type="button"
            label="解除する"
            color="accent"
            className="md:hidden"
            onClick={handleDeletePaypalAccount}
          />
        )}
      </>
    );
  };

  const connectPaypalStatus = () => {
    return isConnectApplying() ? (
      <>
        <ul className="mt-4">
          <li className="flex gap-2">
            <CreditStatusLabel
              isUse={paypalAccount.user_payment_paypal?.charges_enabled ? 1 : 0}
              />
          </li>
        </ul>
        <div className="mt-5">
          <p>
            <Link
              href={'https://www.paypal.com/jp/webapps/mpp/lp/about-paypal'}
              target="_blank"
              className="inline-block font-normal text-primary-dark hover:underline">
              ペイパルについて
            </Link>
          </p>
          <p>
            <Link
              href={'https://www.paypal.com/jp/webapps/mpp/lp/set-up-bank'}
              target="_blank"
              className="inline-block font-normal text-primary-dark hover:underline">
              銀行口座のご利用について
            </Link>
          </p>
        </div>
      </>
    ) : (
      <div className="mt-5">
        <p>
          <Link
            href={'https://www.paypal.com/jp/webapps/mpp/lp/about-paypal'}
            target="_blank"
            className="inline-block font-normal text-primary-dark hover:underline">
            ペイパルについて
          </Link>
        </p>
        <p>
          <Link
            href={'https://www.paypal.com/jp/webapps/mpp/lp/set-up-bank'}
            target="_blank"
            className="inline-block font-normal text-primary-dark hover:underline">
            銀行口座のご利用について
          </Link>
        </p>
      </div>
    );
  };

  const editLink = () => {
    const PayPalMode = process.env.NEXT_PUBLIC_PAYPAL_MODE ?? "sandbox";
    const PayPalLinkRevoke = (PayPalMode === "live")
     ? "https://www.paypal.com/businessmanage/account/accountAccess" 
     : "https://www.sandbox.paypal.com/businessmanage/account/accountAccess"; 

    return isChargeEnabled() ? (
      <ButtonText
        target="_blank"
        href={PayPalLinkRevoke}
        type="button"
        label="PayPalの管理画面で解除する"
        color="accent"
        className="invisible md:visible"
        onClick={handleDeletePaypalAccount}
      />
    ) : isConnectApplying() ? (
      <Button
        type="button"
        label="PayPal決済申請ページへ"
        size="sm-fix"
        spSize="lg-fix"
        className="mt-4 md:mt-0"
        onClick={handleCreatePaypalAccount}
      />
    ) : (
      <Button
        type="button"
        label="PayPal決済申請ページへ"
        size="sm-fix"
        spSize="lg-fix"
        className="mt-4 md:mt-0"
        onClick={handleCreatePaypalAccount}
      />
    );
  };

  const connectInfo = () => {
    return (
      <ul className="flex-row gap-2 items-center md:flex">
        <li className="flex-auto">
          <p>PayPal決済の利用にはPayPalアカウント口座の作成と連携が必要です</p>
          <p>PayPal連携設定や接続解除は、反映されるまで約2〜5分程度かかる場合があります。</p>
          <p>連携完了したら、「利用可能」ラベルが表示されます。</p>
          <p>ご不明な点がある場合は、Paypalにお問い合わせください。</p>
          {connectPaypalStatus()}
        </li>
        <li className="flex-none text-right md:w-[220px]">{editLink()}</li>
      </ul>
    );
  };

  const Lists = [
    {
      dt: connectTitle("PayPalの利用申請"),
      dd: connectInfo(),
    },
  ];
  return <AccountsListCard title={'PayPalの決済設定'} list={Lists} />;
};
